import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Skeleton,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { headerSty, contentSty } from "../table-styles";
import { PATH_MEDIC } from "../../routes/path";
import { MedicTableProps } from "../../interface/medic";
import { formatDate, formatTextType, limitToSeven } from "../../utils/url-id";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";

interface ExtendedMedicTableProps extends MedicTableProps {
  onSuspend?: (user: any) => void;
  onDelete?: (user: any) => void;
}

export default function MedicTable({
  data,
  loading,
  onSuspend,
  onDelete,
}: ExtendedMedicTableProps) {
  const theme = useTheme();
  const headStyles = { ...headerSty, color: theme.palette.neutral.main };
  const contentStyles = { ...contentSty };
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = React.useState<any>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, user: any) => {
    event.stopPropagation(); // Prevent row click
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSuspend = () => {
    if (selectedUser && onSuspend) {
      onSuspend(selectedUser);
    }
    handleCloseMenu();
  };

  const handleDelete = () => {
    if (selectedUser && onDelete) {
      onDelete(selectedUser);
    }
    handleCloseMenu();
  };

  const handleRowClick = (medicId: string) => {
    navigate(`${PATH_MEDIC.root}/${medicId}`);
  };

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          my: 1,
          background: theme.palette.common.white,
          borderRadius: "16px",
          border: 0,
          boxShadow: "none",
          minHeight: 400,
        }}
      >
        <Table
          sx={{
            minWidth: 650,
            boxShadow: "none",
            my: 2,
            paddingRight: 2,
            border: 0,
          }}
          aria-label="simple table"
        >
          <TableHead sx={{ paddingX: 2, border: 0 }}>
            <TableRow>
              <TableCell sx={{ ...headStyles }}>ID</TableCell>
              <TableCell sx={{ ...headStyles, width: "15%" }}>Name</TableCell>
              <TableCell
                sx={{ ...headStyles, color: theme.palette.neutral.main }}
              >
                Medic Type
              </TableCell>
              <TableCell sx={{ ...headStyles }}>Gender</TableCell>
              <TableCell sx={{ ...headStyles }}>Status</TableCell>
              <TableCell sx={{ ...headStyles }}>Verification</TableCell>
              <TableCell sx={{ ...headStyles }}>Date Joined</TableCell>
              {(onSuspend || onDelete) && (
                <TableCell sx={{ ...headStyles }}>Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row: any) => (
              <TableRow
                key={row?._id}
                onClick={() => handleRowClick(row?._id)}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <TableCell sx={contentSty}>
                  {limitToSeven(row?._id ?? "")}
                </TableCell>
                <TableCell sx={{ ...contentStyles }}>
                  {formatTextType(row?.first_name) +
                    " " +
                    formatTextType(row?.last_name)}
                </TableCell>
                <TableCell sx={{ ...contentStyles }}>
                  {formatTextType(row?.medic_type)}
                </TableCell>
                <TableCell sx={{ ...contentStyles }}>
                  {formatTextType(row?.gender)}
                </TableCell>
                <TableCell sx={{ ...contentStyles }}>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Chip
                      label={formatTextType(
                        row?.employment_details?.status ?? "Available",
                      )}
                      size="small"
                      color={
                        row?.employment_details?.status === "employed"
                          ? "success"
                          : "default"
                      }
                    />
                    {row?.suspended && (
                      <Chip
                        label="Suspended"
                        color="error"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ ...contentStyles }}>
                  <Chip
                    label={formatTextType(row?.verification ?? "Unverified")}
                    size="small"
                    color={
                      row?.verification === "vetted" ? "success" : "warning"
                    }
                  />
                </TableCell>
                <TableCell sx={{ ...contentStyles }}>
                  {formatDate(row?.created_at)}
                </TableCell>
                {(onSuspend || onDelete) && (
                  <TableCell sx={{ ...contentStyles }}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleOpenMenu(e, row)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data?.length === 0 && !loading && (
          <Box
            sx={{
              height: 200,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h4"
              sx={{ fontWeight: 600, color: theme.palette.neutral.main }}
            >
              No Medic data available.
            </Typography>
          </Box>
        )}
        {loading && (
          <Box sx={{ width: "96%", mx: "auto" }}>
            <Skeleton
              variant="rounded"
              width={"100%"}
              height={60}
              sx={{ my: 2 }}
            />
            <Skeleton
              variant="rounded"
              width={"100%"}
              height={60}
              sx={{ my: 2 }}
            />
            <Skeleton
              variant="rounded"
              width={"100%"}
              height={60}
              sx={{ my: 2 }}
            />
            <Skeleton
              variant="rounded"
              width={"100%"}
              height={60}
              sx={{ my: 2 }}
            />
          </Box>
        )}
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        {onSuspend && (
          <MenuItem onClick={handleSuspend}>
            {selectedUser?.suspended ? "Unsuspend User" : "Suspend User"}
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
            Delete User
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
